import { BBBackendQueryOptions, type BidBattle } from "@bidbattles/bloks/lib/rapace";
import { paginationSearchParamsSchema } from "@bidbattles/bloks/lib/rapace/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { ButtonLink } from "@bidbattles/bloks/components/ui/button";
import { DataTable } from "@bidbattles/bloks/components/ui/table";
import { BidBattleImageWrapper } from "@/components/battle";
import { formatCurrency } from "@/lib/utils";
import { z } from "zod/v4";
import { cn } from "@bidbattles/bloks/lib/ui";
import { useUser } from "@/hooks/user";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { m } from "@/paraglide/messages";

export const Route = createFileRoute("/settings/battles")({
	loaderDeps: ({ search }) => ({ pagination: search }),
	async loader({ context, deps: { pagination } }) {
		await context.queryClient.ensureQueryData(
			BBBackendQueryOptions.listBattles({ ...pagination, reserved: true }),
		);
	},
	validateSearch: zodValidator(
		z.intersection(
			paginationSearchParamsSchema,
			z.object({
				winner: z.boolean().optional(),
			}),
		),
	),
	component: RouteComponent,
});

const columnHelper = createColumnHelper<BidBattle>();

function RouteComponent() {
	const paginationSearchParams = Route.useSearch();
	const {
		data: { battles, pagination },
	} = useSuspenseQuery(
		BBBackendQueryOptions.listBattles({
			...paginationSearchParams,
			reserved: true,
		}),
	);
	const user = useUser();

	const columns = useMemo(
		() => [
			columnHelper.display({
				id: "prize",
				header: "Prize",
				cell: (info) => {
					const row = info.row.original;
					return (
						<div className="inline-flex items-center gap-4">
							<BidBattleImageWrapper>
								<img
									alt={row.item_name}
									src={row.item_image_url}
									className="size-12 aspect-square object-contain"
									loading="lazy"
								/>
							</BidBattleImageWrapper>
							<div className="flex flex-col gap-2">
								<p className="font-semibold">{row.item_name}</p>
								<p className="text-gray-500">{row.item_description}</p>
							</div>
						</div>
					);
				},
			}),

			columnHelper.display({
				id: "status",
				header: "Status",
				cell: (info) => {
					const row = info.row.original;
					return m[`bidbattle__status__${row.status}`]();
				},
			}),

			columnHelper.accessor("total_seats", {
				header: "Players",
				cell: (info) => info.getValue(),
			}),

			columnHelper.accessor("entry_price", {
				header: "Admission",
				cell: (info) => formatCurrency("EUR", info.getValue()),
			}),

			columnHelper.accessor("winner_details", {
				header: "Winner",
				cell: (info) => {
					const winner_details = info.getValue();
					if (!winner_details) {
						return null;
					}

					return (
						<span
							className={cn(winner_details.id === user?.id && "text-green-400")}
						>
							{winner_details.id === user?.id ? "You" : winner_details.name}
						</span>
					);
				},
			}),

			columnHelper.display({
				id: "actions",
				header: () => <span className="sr-only">Actions</span>,
				cell: (info) => {
					const row = info.row.original;
					return (
						<ButtonLink variant="link" to="/battle/$id" params={{ id: row.id }}>
							View battle
						</ButtonLink>
					);
				},
			}),
		],
		[user?.id],
	);

	return (
		<>
			<DataTable
				hideFooters
				columns={columns}
				data={battles}
				pagination={pagination}
			/>
		</>
	);
}
